import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

interface TimePeriodDropdownProps {
    selectedPeriod: string;
    onPeriodChange: (period: string) => void;
}

const timePeriodGroups = {
    Week: ["This week", "Previous week", "Next 2 weeks"],
    Month: ["Month to date", "Previous month", "Last 30 days"],
    Quarter: ["Quarter to date", "Previous quarter", "Last 90 days"],
    Year: ["Last 6 months", "Last 12 months", "Year to date"]
};

const TimePeriodDropdown = ({ selectedPeriod, onPeriodChange }: TimePeriodDropdownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (period: string) => {
        onPeriodChange(period);
        setIsOpen(false);
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className=" statusbtn-user flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-surface-bg-secondary text-text-text-secondary hover:bg-surface-border transition-colors min-w-[180px] justify-between"
            >
                <span className="font-semibold">{selectedPeriod}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-[220px] bg-white rounded-lg shadow-lg border border-surface-border z-50 max-h-[400px] overflow-y-auto">
                    {Object.entries(timePeriodGroups).map(([groupName, periods]) => (
                        <div key={groupName} className="py-2">
                            <div className="px-4 py-1 text-xs font-semibold text-text-text-secondary uppercase userprofil-btn-title">
                               <p className="dark:text-white my-0 text-lg"> {groupName}</p>
                            </div>
                            {periods.map((period) => (
                                <button
                                    key={period}
                                    onClick={() => handleSelect(period)}
                                    className={`w-full text-left px-4 py-2 text-sm hover:bg-surface-bg-secondary transition-colors inner-userprofil-btn ${
                                        selectedPeriod === period
                                            ? 'bg-main-blue text-white hover:bg-blue-600'
                                            : 'text-text-primary'
                                    }`}
                                >
                                    {period}
                                </button>
                            ))}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default TimePeriodDropdown;
